import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import ctl from '@netlify/classnames-template-literals'
import { useAtom, useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef } from 'react'
import { highlightedLayerIdAtom, selectedLayerIdAtom } from '../atoms'
import { useCanvasStore } from '../canvas-store'
import type { Layer, LayerNode, LayerType } from '../data/v1'
import { CONTAINER_TYPES } from '../data/v1'
import { useDesignerActions } from '../use-designer-actions'
import { DropSlot, type HotTarget, slotKey } from './layer-list-dnd'
import { LockToggle, TypeBadge, VisibilityToggle } from './layer-list-ui'

function isContainerType(type: LayerType): boolean {
	return CONTAINER_TYPES.has(type)
}

export type NodeViewProps = {
	node: LayerNode
	parentId: string | null
	idx: number
	layer: Layer
	draggingId: string | null
	draggingType: LayerType | null
	hotTarget: HotTarget
	expanded: Set<string>
	onToggleExpand: (id: string) => void
	layers: Record<string, Layer>
	inheritedHidden: boolean
	inheritedLocked: boolean
}

export function NodeView({
	node,
	parentId,
	idx,
	layer,
	draggingId,
	draggingType,
	hotTarget,
	expanded,
	onToggleExpand,
	layers,
	inheritedHidden,
	inheritedLocked,
}: NodeViewProps) {
	const headerRef = useRef<HTMLDivElement>(null)
	const store = useCanvasStore()
	const [selectedId, setSelectedId] = useAtom(selectedLayerIdAtom, { store })
	const setHighlightedLayerId = useSetAtom(highlightedLayerIdAtom, { store })
	const { updateLayer } = useDesignerActions()

	const isContainer = isContainerType(layer.type)
	const isExpanded = isContainer && expanded.has(node.id)
	const isDragging = draggingId === node.id
	const isSelected = selectedId === node.id
	const isHidden = layer.hidden
	const effectivelyHidden = isHidden || inheritedHidden
	const isLocked = layer.locked
	const effectivelyLocked = isLocked || inheritedLocked

	const containerHot = isContainer && hotTarget !== null && hotTarget.slotKey.startsWith(`${node.id}-`)

	// Register as draggable
	useEffect(() => {
		const el = headerRef.current
		if (!el) { return }
		return draggable({
			element: el,
			getInitialData: () => ({ id: node.id, type: layer.type }),
		})
	}, [node.id, layer.type])

	// Register row as drop target — proxies to adjacent slots based on cursor position
	useEffect(() => {
		const el = headerRef.current
		if (!el || !draggingId) { return }
		return dropTargetForElements({
			element: el,
			getData: ({ input, element }) => {
				const rect = element.getBoundingClientRect()
				const ratio = (input.clientY - rect.top) / rect.height
				const topHalf = ratio < 0.5

				if (isContainer && !isExpanded) {
					// Collapsed container: drop anywhere → insert as last child
					return {
						_kind: 'slot',
						parentId: node.id,
						idx: node.children.length,
						slotKey: slotKey(node.id, node.children.length),
					}
				}

				if (isContainer && isExpanded) {
					// Open container: top 50% → slot before, bottom 50% → first child slot
					if (topHalf) {
						return {
							_kind: 'slot',
							parentId,
							idx,
							slotKey: slotKey(parentId, idx),
						}
					}
					return {
						_kind: 'slot',
						parentId: node.id,
						idx: 0,
						slotKey: slotKey(node.id, 0),
					}
				}

				// Leaf: top 50% → slot before, bottom 50% → slot after
				if (topHalf) {
					return {
						_kind: 'slot',
						parentId,
						idx,
						slotKey: slotKey(parentId, idx),
					}
				}
				return {
					_kind: 'slot',
					parentId,
					idx: idx + 1,
					slotKey: slotKey(parentId, idx + 1),
				}
			},
			canDrop: ({ source }) => source.data.id !== node.id,
		})
	}, [node.id, node.children.length, layer.type, isContainer, isExpanded, parentId, idx, draggingId])

	const handleSelect = useCallback(() => {
		setSelectedId(node.id)
	}, [node.id, setSelectedId])

	const handleToggleHidden = useCallback((e: React.MouseEvent) => {
		e.stopPropagation()
		if (inheritedHidden) { return }
		updateLayer(node.id, { hidden: !layer.hidden })
	}, [node.id, layer.hidden, inheritedHidden, updateLayer])

	const handleToggleLocked = useCallback((e: React.MouseEvent) => {
		e.stopPropagation()
		if (inheritedLocked) { return }
		updateLayer(node.id, { locked: !layer.locked })
	}, [node.id, layer.locked, inheritedLocked, updateLayer])

	const sharedProps = {
		draggingId,
		draggingType,
		hotTarget,
	}

	if (isContainer) {
		return (
			<div
				className={ctl(`duration-100 ${isDragging ? 'opacity-30' : ''}`)}
			>
				<div
					className={ctl(`
						rounded-md border duration-100
						ring
						${
						containerHot
							? 'border-white ring-blue-400/80 bg-blue-300/20'
							: 'border-white/70 ring-grey-200/80 bg-grey-100/30'
					}
					`)}
				>
					<div
						ref={headerRef}
						onClick={handleSelect}
						onMouseEnter={() => setHighlightedLayerId(node.id)}
						onMouseLeave={() => setHighlightedLayerId(null)}
						className={ctl(`
							group/layer-row flex h-7 cursor-default items-center gap-1 px-1.5 text-xs select-none
							${isExpanded ? 'rounded-t-md' : 'rounded-md'}
							${isSelected ? 'bg-blue-200 text-blue-900' : 'text-grey-700 hover:bg-purple-200'}
							${effectivelyHidden ? 'opacity-40' : ''}
						`)}
					>
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation()
								onToggleExpand(node.id)
							}}
							className="flex size-4 shrink-0 items-center justify-center rounded hover:bg-grey-100"
						>
							<svg
								width="8"
								height="8"
								viewBox="0 0 8 8"
								className={ctl(`transition-transform duration-100 ${isExpanded ? 'rotate-90' : ''}`)}
							>
								<path
									d="M2.5 1.5l3 2.5-3 2.5"
									stroke="currentColor"
									strokeWidth="1.3"
									fill="none"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
						<TypeBadge type={layer.type} />
						<span className={ctl(`min-w-0 flex-1 truncate ${effectivelyHidden ? 'line-through' : ''}`)}>
							{node.id}
						</span>
						<LockToggle locked={isLocked} inherited={inheritedLocked} onClick={handleToggleLocked} />
						<VisibilityToggle hidden={isHidden} inherited={inheritedHidden} onClick={handleToggleHidden} />
					</div>

					{isExpanded && (
						<div className="min-h-2 rounded bg-grey-950/50 pl-4 pr-1">
							<DropSlot
								parentId={node.id}
								idx={0}
								biggerTarget={node.children.length === 0}
								{...sharedProps}
							/>
							{node.children.map((child, i) => {
								const childLayer = layers[child.id]
								if (!childLayer) { return null }
								return (
									<div key={child.id}>
										<NodeView
											node={child}
											parentId={node.id}
											idx={i}
											layer={childLayer}
											expanded={expanded}
											onToggleExpand={onToggleExpand}
											layers={layers}
											inheritedHidden={effectivelyHidden}
											inheritedLocked={effectivelyLocked}
											{...sharedProps}
										/>
										<DropSlot parentId={node.id} idx={i + 1} {...sharedProps} />
									</div>
								)
							})}
						</div>
					)}
				</div>
			</div>
		)
	}

	// Leaf node
	return (
		<div
			ref={headerRef}
			onClick={handleSelect}
			onMouseEnter={() => setHighlightedLayerId(node.id)}
			onMouseLeave={() => setHighlightedLayerId(null)}
			className={ctl(`
				group/layer-row flex h-7 cursor-default items-center gap-1 rounded px-1.5 pl-6 text-xs select-none
				${isDragging ? 'opacity-30' : ''}
				${isSelected ? 'bg-blue-200 text-blue-900' : 'text-grey-700 hover:bg-purple-200'}
				${effectivelyHidden ? 'opacity-40' : ''}
			`)}
		>
			<TypeBadge type={layer.type} />
			<span className={ctl(`min-w-0 flex-1 truncate ${effectivelyHidden ? 'line-through' : ''}`)}>{node.id}</span>
			<LockToggle locked={isLocked} inherited={inheritedLocked} onClick={handleToggleLocked} />
			<VisibilityToggle hidden={isHidden} inherited={inheritedHidden} onClick={handleToggleHidden} />
		</div>
	)
}
